import { ExportOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from
  'react-query';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps, FilterValueProps, mappingFilterFields,
  mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import { OnlineStatusLabel } from 'common/components/StatusLabel';
import { exportTrackingService, getTrackingService } from 'common/services/tracking';
import { TTrackingData } from 'common/services/tracking/types';
import { formatDateTime } from 'common/utils/functions';
import roles from 'configs/roles';

export type TTrackingColumnData = TTrackingData['trackingData'];

const TrackingManagement: React.FC<ActiveRoles> = ({
  roleOther
}) => {
  /* Hooks */
  const { t } = useTranslation();

  /* Selectors */
  const {
    defaultPageSize,
    advancedFilter,
  } = useAppSelector((state) => state.system);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());

  /* States */
  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  /* Queries */
  const { data: trackingRes, isLoading } = useQuery(
    ['getAllTracking', currentPage, currentView, keyword, returnParamsFilter],
    () => getTrackingService({
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter
    }),
  );

  const { mutate: exportMutate, isLoading: exportLoading } = useMutation(
    ['getTrackingExportLink', currentPage, currentView, keyword, returnParamsFilter],
    () => exportTrackingService({
      ...returnParamsFilter
    }),
    {
      onSuccess(res) {
        if (res.data.link) {
          window.open(res.data.link, '_blank');
        }
      },
    }
  );

  /* Variables */
  const columns: ColumnsType<TTrackingColumnData> = [
    {
      title: 'ID',
      key: 'id',
      width: 55,
      align: 'center',
      fixed: 'left',
      sorter: {
        compare: (a: TTrackingColumnData, b: TTrackingColumnData) => a.id - b.id,
      },
      sortDirections: ['descend', 'ascend'],
      render: (_name: string, data: TTrackingColumnData) => (
        <Typography.Text>
          {data.id}
        </Typography.Text>
      ),
    },
    {
      title: t('tracking.uuid'),
      dataIndex: 'uuid',
      key: 'uuid',
      render: (_: string, data: TTrackingColumnData) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.uuid}
        </Typography.Text>
      ),
    },
    {
      title: t('tracking.kioskCode'),
      dataIndex: 'kioskCode',
      key: 'kioskCode',
      render: (_: string, data: TTrackingColumnData) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.kioskCode}
        </Typography.Text>
      ),
    },
    {
      title: t('tracking.kiosk'),
      dataIndex: 'kiosk',
      key: 'kiosk',
      render: (_: string, data: TTrackingColumnData) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.kiosk}
        </Typography.Text>
      ),
    },
    {
      title: t('tracking.step'),
      dataIndex: 'step',
      key: 'step',
      render: (_: string, data: TTrackingColumnData) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.step}
        </Typography.Text>
      ),
    },
    {
      title: t('tracking.onlineStatus'),
      dataIndex: 'onlineStatus',
      key: 'onlineStatus',
      render: (_: string, data: TTrackingColumnData) => (
        <OnlineStatusLabel status={data.isOnline} />
      ),
    },
    // Ngày submitted
    {
      title: t('tracking.submittedAt'),
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      sorter: {
        compare: (a: TTrackingColumnData, b: TTrackingColumnData) => {
          const aDate = new Date(a.submittedAt);
          const bDate = new Date(b.submittedAt);
          return Number(aDate) - Number(bDate);
        },
      },
      sortDirections: ['descend', 'ascend'],
      render: (_: string, data: any) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {formatDateTime(data.submittedAt)}
        </Typography.Text>
      ),
    },
    // --- Ngày tạo
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: {
        compare: (a: TTrackingColumnData, b: TTrackingColumnData) => {
          const aDate = new Date(a.createdAt);
          const bDate = new Date(b.createdAt);
          return Number(aDate) - Number(bDate);
        },
      },
      sortDirections: ['descend', 'ascend'],
      render: (_: string, data: any) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {formatDateTime(data.createdAt)}
        </Typography.Text>
      ),
    },
  ];

  const resultData: TTrackingColumnData[] = useMemo(() => {
    if (trackingRes?.data && trackingRes?.data.length > 0) {
      return trackingRes?.data.map((item) => ({
        ...item.trackingData,
        kioskCode: item.trackingData.kioskCode || t('kioskManagement.website'),
      }));
    }
    return [];
  }, [trackingRes, t]);

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, {
      ...data,
      index: counter
    }]);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('surveyTracking', advancedFilter),
    [advancedFilter]
  );

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  useEffect(() => {
    setSearchParams(mappingFilterToQuery(selectedFilterList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterList]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.trackings')}
        rightHeader={(
          <Button
            type="primary"
            loading={exportLoading}
            disabled={!roleOther.includes(roles.SURVEY_TRACKING_EXPORT)}
            onClick={() => exportMutate()}
          >
            <ExportOutlined />
            {t('reportsManagement.buttonExport')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          isLoading={isLoading}
          handleSearch={setKeyword}
          tableProps={{
            initShowColumns: ['id', 'uuid', 'kioskCode', 'kiosk', 'onlineStatus', 'step', 'submittedAt', 'createdAt'],
            columns,
            pageData: resultData,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: trackingRes?.meta.total || 1,
            noBaseCol: true,
            filterFields,
          }}
          noCheckbox
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
        />
      </div>
    </>
  );
};

export default TrackingManagement;
